import LiveKitPrimaryButton from '../../components/LiveKitPrimaryButton';
import LiveKitStepper from '../../components/LiveKitStepper';
import { formatUserName } from '../../utils/utils';

import { Card, DateUtils, Flex, Icon, Link, Separator, Text } from '@amigoapp/doca-react';

const TermsModal = ({ consentTerm, provider, onClose }) => (
	<div className="lk-modal-overlay">
		<div className="lk-modal">
			<Text as="h3">Termo de Uso e Consentimento</Text>
			<Text className="doca-mb-4" size="extra-small" variant="gray" weight="light">Resolução CFM n°. 2.314</Text>
			<Separator className="doca-mb-4" />
			<Text className="lk-modal__body" loading={!consentTerm} size="small" variant="gray" weight="light">
				{consentTerm || `Aguarde, carregando o termo de ${formatUserName(provider.title, provider.name)}...`}
			</Text>
			<Flex className="doca-mt-6" gap={3} justify="end">
				<button className="lk-secondary-btn" type="button" onClick={onClose}>Fechar</button>
				<LiveKitPrimaryButton onClick={onClose}>Li e Aceito</LiveKitPrimaryButton>
			</Flex>
		</div>
	</div>
);

const AI_POINTS = [{
	icon: 'user-doctor',
	text: 'A IA é apenas apoio. Quem revisa, decide e assina é o profissional.'
}, {
	icon: 'lock',
	text: 'O áudio é usado apenas para preparar os registros desta consulta e tratado com sigilo conforme a Lei Geral de Proteção de Dados.'
}, {
	icon: 'hand',
	text: 'Você pode recusar agora ou pedir a interrupção a qualquer momento, sem prejuízo ao atendimento.'
}];

const doctorSubtitle = provider => [
	provider.specialty,
	provider.council_name && provider.council_number ? `${provider.council_name} ${provider.council_number}` : null
].filter(Boolean).join(' · ');

const LiveKitConsent = ({
	appointmentDate,
	consentTerm,
	fileInputRef,
	files,
	isSubmittingFile,
	loading,
	provider,
	showTerms,
	step,
	onAcceptAi,
	onBack,
	onBackToFiles,
	onCloseTerms,
	onContinuar,
	onDeclineAi,
	onOpenTerms,
	onProsseguir,
	onRemoveFile,
	onSelectFiles
}) => (
	<Flex align="center" className="lk-consent-page doca-px-4" direction="col" flex={1} gap={4} justify="between">
		<Card className="lk-surface-card lk-consent-card">
			<LiveKitStepper activeStep={step} />

			{step === 'termo' && (
				<>
					<div className="lk-consent__eyebrow">
						<Icon className="lk-consent__eyebrow-icon" name="video" type="solid" />
						<span>Teleconsulta</span>
					</div>
					<p className="lk-consent__date">
						{DateUtils.format(appointmentDate, { format: 'ddd, DD [de] MMMM [-] HH:mm[h]' })}
					</p>

					<Separator className="lk-consent__rule" />

					<p className="lk-consent__intro">
						Você está prestes a iniciar seu teleatendimento com {formatUserName(provider.title, provider.name)}.
					</p>

					<div className="lk-consent__doctor">
						<div className="lk-consent__doctor-avatar">
							<Icon name="user-doctor" type="solid" />
						</div>
						<div>
							<p className="lk-consent__doctor-name">{formatUserName(provider.title, provider.name)}</p>
							<p className="lk-consent__doctor-subtitle">{doctorSubtitle(provider)}</p>
						</div>
					</div>

					<p className="lk-consent__terms">
						Ao clicar em prosseguir abaixo, eu aceito o{' '}
						<Link href="#" onClick={onOpenTerms}>Termo de Uso e Consentimento</Link>.
					</p>

					<LiveKitPrimaryButton loading={loading} onClick={onProsseguir}>
						Prosseguir
					</LiveKitPrimaryButton>
				</>
			)}

			{step === 'anexos' && (
				<>
					<span className="lk-back-link" role="button" tabIndex={0} onClick={onBack} onKeyDown={e => e.key === 'Enter' && onBack()}>
						<Icon className="lk-back-link__icon" name="arrow-left" type="solid" />Voltar
					</span>
					<h3 className="lk-attach__title">Anexo de exames e documentos</h3>
					<p className="lk-attach__subtitle">
						Anexe exames ou documentos solicitados por {formatUserName(provider.title, provider.name)}.
					</p>

					<div className="lk-attach__rule" />

					<button className="lk-dropzone" type="button" onClick={() => fileInputRef.current?.click()}>
						<Icon className="lk-dropzone__icon" name="cloud-arrow-up" type="solid" />
						<span className="lk-dropzone__label">{isSubmittingFile ? 'Enviando...' : 'Clique para selecionar arquivos'}</span>
						<input ref={fileInputRef} multiple className="hidden-input" name="upload" type="file" onChange={onSelectFiles} />
					</button>

					{files.length > 0 && (
						<div className="lk-file-list">
							{files.map(file => (
								<div key={file.id} className="lk-file-list__item">
									<Icon className="lk-file-list__icon" name="file-lines" type="regular" />
									<span className="lk-file-list__name">{file.name}</span>
									<span className="lk-file-list__size">{file.sizeLabel}</span>
									<Icon className="lk-file-list__remove" name="xmark" type="solid" onClick={() => onRemoveFile(file.id)} />
								</div>
							))}
						</div>
					)}

					<div className="lk-attach__actions">
						<LiveKitPrimaryButton disabled={isSubmittingFile} onClick={onContinuar}>
							Continuar
						</LiveKitPrimaryButton>
					</div>
				</>
			)}
			{step === 'ia' && (
				<>
					<span className="lk-back-link" role="button" tabIndex={0} onClick={onBackToFiles} onKeyDown={e => e.key === 'Enter' && onBackToFiles()}>
						<Icon className="lk-back-link__icon" name="arrow-left" type="solid" />Voltar
					</span>

					<div className="lk-ai__heading">
						<div className="lk-ai__badge">
							<Icon name="sparkle" type="solid" />
						</div>
						<h3 className="lk-attach__title lk-ai__title">Uso de IA na consulta</h3>
					</div>

					<p className="lk-attach__subtitle lk-ai__subtitle">
						Ao aceitar, você autoriza {formatUserName(provider.title, provider.name)} a usar recursos
						de inteligência artificial que ouve a teleconsulta para preparar rascunhos dos registros
						no seu prontuário.
					</p>

					<ul className="lk-ai__points">
						{AI_POINTS.map(point => (
							<li key={point.icon} className="lk-ai__point">
								<Icon className="lk-ai__point-icon" name={point.icon} type="regular" />
								<span>{point.text}</span>
							</li>
						))}
					</ul>

					<div className="lk-attach__actions lk-ai__actions">
						<LiveKitPrimaryButton loading={loading} onClick={onAcceptAi}>
							Aceito o uso de IA
						</LiveKitPrimaryButton>
						<button className="lk-secondary-btn lk-ai__decline" disabled={loading} type="button" onClick={onDeclineAi}>
							Não aceito
						</button>
					</div>
				</>
			)}

		</Card>

		{showTerms && <TermsModal consentTerm={consentTerm} provider={provider} onClose={onCloseTerms} />}

		<img alt="Amigo - Logo" className="lk-brand-logo doca-mb-8" src="https://cdn.amigotech.com.br/logos/amigo.svg" />
	</Flex>
);

export default LiveKitConsent;
